import {
    ChangeDetectionStrategy,
    Component,
    ComponentFactoryResolver,
    ElementRef,
    Input,
    Type,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import {Observable} from 'rxjs';
import {AdditionalBlockButton} from '@theme/components';

export interface NestedSelectDropdown {
    button: AdditionalBlockButton;
    options: DropdownOption[];
}

export interface DropdownOption {
    label: string;
    disabled?: Observable<boolean>;
    callback?: () => void;
    children?: DropdownOption[];
    tooltip?: string;
    openPopover?: boolean; // flag to differentiate behavior - open popover @popoverContent instead of calling @callback
    popoverComponent?: Type<any>; // Component constructor
    popoverData?: any; // Optional data to pass to the component
}

@Component({
    selector: 'ngx-nested-select-dropdown',
    templateUrl: './nested-select-dropdown.component.html',
    styleUrls: ['./nested-select-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NestedSelectDropdownComponent {
    @ViewChild('buttonTarget', {static: true}) buttonTarget: ElementRef<HTMLElement>;
    @ViewChild('popoverContainer', {read: ViewContainerRef}) popoverContainer!: ViewContainerRef;

    showingPopover = false;

    @Input() config: NestedSelectDropdown;

    isDropdownDisplayed: boolean = false;

    justClickedInside = false;
    justClickedInsidePopover = false;

    constructor(private cfr: ComponentFactoryResolver) {}

    onClickOutside(event): void {
        // We use `justClickedInside` as a short-lived flag to ignore the first clickOutside event
        // immediately following a user interaction (e.g., clicking the dropdown button).
        // This is necessary because ngxClickOutside triggers *after* the click,
        // so without this guard, clicking the button would immediately close the dropdown.
        if (this.justClickedInside) {
            this.justClickedInside = false;
            return;
        }

        const host = this.buttonTarget?.nativeElement;
        if (!host) return; // host not ready (shouldn't happen with { static: true } but safe)

        const target = (event as Event)?.target ?? null;
        if (!(target instanceof Node) || !host.contains(target)) {
            this.isDropdownDisplayed = false;
        }
    }

    onClickOutsidePopover(event): void {
        // `justClickedInsidePopover` is a short-lived flag used to prevent the popover from being
        // immediately closed by the `ngxClickOutside` directive. This situation can occur because
        // Angular processes the `clickOutside` event after the click that triggered the popover.
        // Without this flag, clicking a dropdown option that opens a popover would instantly close it.
        // The flag is reset on the next `clickOutsidePopover` event cycle.
        if (this.justClickedInsidePopover) {
            this.justClickedInsidePopover = false;
            return;
        }
        const host = this.buttonTarget?.nativeElement;
        if (!host) return; // host not ready (shouldn't happen with { static: true } but safe)

        const target = (event as Event)?.target ?? null;

        if (!(target instanceof Node) || !host.contains(target)) {
            this.showingPopover = false;
            this.popoverContainer?.clear();
        }
    }

    toggleDropdown(): void {
        this.justClickedInside = true;
        this.isDropdownDisplayed = !this.isDropdownDisplayed;
    }

    onOptionClick(option: DropdownOption, event: MouseEvent): void {
        event.preventDefault();

        if (option.openPopover && option.popoverComponent) {
            this.isDropdownDisplayed = false;
            this.showingPopover = true;
            this.justClickedInsidePopover = true;

            this.popoverContainer.clear();
            const factory = this.cfr.resolveComponentFactory(option.popoverComponent);
            const componentRef = this.popoverContainer.createComponent(factory);

            // Optionally pass data to the component
            if (componentRef.instance) {
                // Add a close function to popoverData and pass it in
                const popoverData = {
                    ...(option.popoverData || {}),
                    closePopover: () => {
                        this.showingPopover = false;
                        this.popoverContainer.clear();
                    },
                };

                Object.assign(componentRef.instance, popoverData);
            }

            return;
        }

        if (!option.children) {
            option.callback?.();
            this.toggleDropdown();
        }
    }
}
